// Saved views for the v2 grid (phase 4).
//
// Per-tenant, per-entity (people / companies / lists) named bookmarks
// that snapshot the current URL search params. Persisted to
// localStorage so they survive reloads but stay private to one
// browser. Server-side persistence (extension to TenantSettings) is a
// follow-up; for v1 of saved views, local is enough.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface SavedView {
  id: string;
  name: string;
  /** URL search-params string, e.g. "q=acme&sort=updated". */
  params: string;
}

interface SavedViewsBarProps {
  /** A stable scope key per tenant + entity, e.g. `${tenantId}:people`. */
  scope: string;
}

const KEY_PREFIX = 'cc.savedviews.';

function loadAll(scope: string): SavedView[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + scope);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as SavedView[]) : [];
  } catch {
    return [];
  }
}

function persist(scope: string, views: SavedView[]) {
  window.localStorage.setItem(KEY_PREFIX + scope, JSON.stringify(views));
}

export function SavedViewsBar({ scope }: SavedViewsBarProps) {
  const [params, setParams] = useSearchParams();
  const [views, setViews] = useState<SavedView[]>(() => loadAll(scope));
  const [naming, setNaming] = useState(false);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    setViews(loadAll(scope));
  }, [scope]);

  const currentParams = params.toString();
  const activeView = views.find((v) => v.params === currentParams);
  const dirty = currentParams !== '' && !activeView;

  function applyView(view: SavedView) {
    setParams(new URLSearchParams(view.params), { replace: true });
  }

  function applyAll() {
    setParams(new URLSearchParams(), { replace: true });
  }

  function commitSave() {
    const name = draft.trim();
    if (!name) {
      setNaming(false);
      return;
    }
    const next: SavedView[] = [
      ...views,
      { id: crypto.randomUUID(), name, params: currentParams }
    ];
    setViews(next);
    persist(scope, next);
    setDraft('');
    setNaming(false);
  }

  function deleteView(id: string) {
    const next = views.filter((v) => v.id !== id);
    setViews(next);
    persist(scope, next);
  }

  return (
    <div className="flex items-center gap-1 flex-wrap text-sm">
      <Chip active={!activeView && currentParams === ''} onClick={applyAll}>
        All
      </Chip>
      {views.map((v) => (
        <ChipWithDelete
          key={v.id}
          active={activeView?.id === v.id}
          onClick={() => applyView(v)}
          onDelete={() => deleteView(v.id)}
        >
          {v.name}
        </ChipWithDelete>
      ))}
      {dirty && !naming && (
        <button
          type="button"
          onClick={() => setNaming(true)}
          className="rounded-md px-2 py-1 text-xs text-mid hover:surface-hover border border-dashed border-input"
          title="Save the current filters as a new view"
        >
          + Save view
        </button>
      )}
      {naming && (
        <span className="inline-flex items-center gap-1">
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitSave();
              } else if (e.key === 'Escape') {
                setDraft('');
                setNaming(false);
              }
            }}
            placeholder="View name"
            className="input text-xs px-2 py-1"
            style={{ width: '8rem' }}
          />
        </span>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-2 py-1 text-xs transition ${
        active ? 'surface-muted text-strong font-medium' : 'text-mid hover:surface-hover'
      }`}
    >
      {children}
    </button>
  );
}

function ChipWithDelete({
  active,
  onClick,
  onDelete,
  children
}: {
  active: boolean;
  onClick(): void;
  onDelete(): void;
  children: React.ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-md transition ${
        active ? 'surface-muted text-strong' : 'hover:surface-hover'
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className={`pl-2 pr-1 py-1 text-xs ${active ? 'font-medium' : 'text-mid'}`}
      >
        {children}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (confirm(`Delete saved view "${children}"?`)) onDelete();
        }}
        className="px-1.5 py-1 text-faint hover:text-mid"
        aria-label="Delete saved view"
        title="Delete saved view"
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </span>
  );
}
